"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useState } from "react";
import type { ReactElement } from "react";
import type { ApiXTdhCollectionsPage } from "@/generated/models/ApiXTdhCollectionsPage";
import { useXtdhCollectionsQuery } from "@/hooks/useXtdhCollectionsQuery";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { XtdhCollectionsControls } from "./collections-controls";
import { useXtdhCollectionsFilters } from "./hooks/useXtdhCollectionsFilters";
import { useXtdhCollectionSelection } from "./hooks/useXtdhCollectionSelection";
import { XtdhCollectionsList } from "./subcomponents/XtdhCollectionsList";
import { XtdhCollectionTokensPanel } from "./collection-tokens";
interface XtdhReceivedSectionProps {
  readonly profileId: string | null;
  readonly pageSize?: number | undefined;
  readonly requireIdentity?: boolean | undefined;
}
const DEFAULT_PAGE_SIZE = 10;
const DEBOUNCE_DELAY = 300;
export default function XtdhReceivedSection(t0) {
  const $ = _c(51);
  const {
    profileId,
    pageSize: t1,
    requireIdentity: t2
  } = t0;
  const pageSize = t1 === undefined ? DEFAULT_PAGE_SIZE : t1;
  const requireIdentity = t2 === undefined ? true : t2;
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, DEBOUNCE_DELAY);
  const {
    activeSortField,
    activeSortDirection,
    apiOrder,
    handleSortChange
  } = useXtdhCollectionsFilters();
  const t3 = requireIdentity ? Boolean(profileId) : true;
  const t4 = debouncedSearchTerm || undefined;
  let t5;
  if ($[0] !== activeSortField || $[1] !== apiOrder || $[2] !== pageSize || $[3] !== profileId || $[4] !== requireIdentity || $[5] !== t3 || $[6] !== t4) {
    t5 = {
      identity: profileId,
      pageSize,
      sortField: activeSortField,
      order: apiOrder,
      enabled: t3,
      requireIdentity,
      collectionName: t4
    };
    $[0] = activeSortField;
    $[1] = apiOrder;
    $[2] = pageSize;
    $[3] = profileId;
    $[4] = requireIdentity;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    collections,
    isLoading,
    isError,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    fetchNextPage,
    errorMessage,
    isEnabled
  } = useXtdhCollectionsQuery(t5);
  const {
    selectedContract,
    handleCollectionSelect,
    clearSelection
  } = useXtdhCollectionSelection();
  let t6;
  bb0: {
    if (!selectedContract) {
      t6 = null;
      break bb0;
    }
    let t7;
    if ($[8] !== selectedContract) {
      t7 = selectedContract.trim().toLowerCase();
      $[8] = selectedContract;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    const normalized = t7;
    let t8;
    if ($[10] !== collections || $[11] !== normalized) {
      t8 = collections.find(collection => (collection.contract?.trim().toLowerCase() ?? "") === normalized) ?? null;
      $[10] = collections;
      $[11] = normalized;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    t6 = t8;
  }
  const selectedCollection = t6;
  let t7;
  if ($[13] !== refetch) {
    t7 = () => {
      refetch().catch(_temp);
    };
    $[13] = refetch;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleRetry = t7;
  let t8;
  if ($[15] !== fetchNextPage || $[16] !== hasNextPage || $[17] !== isFetchingNextPage) {
    t8 = () => {
      if (!hasNextPage || isFetchingNextPage) {
        return;
      }
      fetchNextPage().catch(_temp2);
    };
    $[15] = fetchNextPage;
    $[16] = hasNextPage;
    $[17] = isFetchingNextPage;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleLoadMore = t8;
  const showLoadMore = hasNextPage && isEnabled;
  const controlsDisabled = isLoading || isFetching;
  const isViewingTokens = Boolean(selectedContract);
  const isIdentityScoped = Boolean(profileId);
  let description = "Collections accruing xTDH from grants.";
  if (isViewingTokens) {
    description = "Review tokens and their xTDH accrual.";
  } else {
    if (isIdentityScoped) {
      description = "Collections where this identity accrues xTDH.";
    }
  }
  const t9 = isViewingTokens ? "xTDH Tokens" : "xTDH Collections";
  let t10;
  if ($[19] !== t9) {
    t10 = <h2 className="tw-m-0 tw-text-base tw-font-semibold tw-text-iron-100">{t9}</h2>;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== description) {
    t11 = <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-iron-400">{description}</p>;
    $[21] = description;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11) {
    t12 = <header className="tw-px-6 tw-pt-6">{t10}{t11}</header>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== activeSortDirection || $[27] !== activeSortField || $[28] !== clearSelection || $[29] !== collections || $[30] !== controlsDisabled || $[31] !== errorMessage || $[32] !== handleCollectionSelect || $[33] !== handleLoadMore || $[34] !== handleRetry || $[35] !== handleSortChange || $[36] !== isEnabled || $[37] !== isError || $[38] !== isFetchingNextPage || $[39] !== isIdentityScoped || $[40] !== isLoading || $[41] !== profileId || $[42] !== requireIdentity || $[43] !== searchTerm || $[44] !== selectedCollection || $[45] !== selectedContract || $[46] !== showLoadMore) {
    t13 = selectedContract ? <XtdhCollectionTokensPanel identity={profileId} contract={selectedCollection?.contract ?? selectedContract} normalizedContract={selectedContract} collection={selectedCollection ?? undefined} onBack={clearSelection} requireIdentity={requireIdentity} /> : <>{(collections.length > 0 || searchTerm) && <XtdhCollectionsControls activeSortField={activeSortField} activeSortDirection={activeSortDirection} onSortChange={handleSortChange} searchTerm={searchTerm} onSearchChange={setSearchTerm} isDisabled={controlsDisabled} />}<XtdhCollectionsList isEnabled={isEnabled} isLoading={isLoading} isError={isError} collections={collections} errorMessage={errorMessage} onRetry={handleRetry} selectedContract={selectedContract} onSelectCollection={handleCollectionSelect} isIdentityScoped={isIdentityScoped} />{showLoadMore ? <div className="tw-flex tw-justify-center tw-px-6 tw-pb-6"><button type="button" onClick={handleLoadMore} disabled={isFetchingNextPage} className="tw-rounded-lg tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-900 tw-px-4 tw-py-2 tw-text-sm tw-text-iron-400 tw-transition desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-iron-300">{isFetchingNextPage ? "Loading..." : "Load More"}</button></div> : null}</>;
    $[26] = activeSortDirection;
    $[27] = activeSortField;
    $[28] = clearSelection;
    $[29] = collections;
    $[30] = controlsDisabled;
    $[31] = errorMessage;
    $[32] = handleCollectionSelect;
    $[33] = handleLoadMore;
    $[34] = handleRetry;
    $[35] = handleSortChange;
    $[36] = isEnabled;
    $[37] = isError;
    $[38] = isFetchingNextPage;
    $[39] = isIdentityScoped;
    $[40] = isLoading;
    $[41] = profileId;
    $[42] = requireIdentity;
    $[43] = searchTerm;
    $[44] = selectedCollection;
    $[45] = selectedContract;
    $[46] = showLoadMore;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== t12 || $[49] !== t13) {
    t14 = <section className="tw-space-y-6 tw-rounded-b-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950">{t12}{t13}</section>;
    $[48] = t12;
    $[49] = t13;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  return t14;
}
function _temp2() {}
function _temp() {}